import React, { FC, useCallback, useEffect, useRef } from 'react';
import { Select } from '@beeline.com/bp-design-system';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { filterNotEmpty } from 'utils/data/listHelpers';
import {
  CustomFieldDefinitionEntityTypeModel,
  CustomFieldDefinitionGroupModel,
  CustomFieldDefinitionGroupsModel
} from '@utmost.co/enterprise-models';
import { CustomFieldDefinitionGroupModelInput } from '@utmost.co/enterprise-models';
import { CREATE_CUSTOM_FIELD_GROUP, GET_CUSTOM_FIELD_GROUPS } from './query';

interface Props {
  disabled?: boolean;
  onChange?: (value: Partial<CustomFieldDefinitionGroupModel> | undefined) => void;
  value?: CustomFieldDefinitionGroupModel;
  onChangeError?: (error?: string) => void;
  entityType: CustomFieldDefinitionEntityTypeModel | undefined;
  label?: string;
  dataTest?: string;
}

const CustomGroupPicker: FC<React.PropsWithChildren<Props>> = ({
  dataTest,
  onChange,
  value,
  onChangeError,
  disabled,
  entityType,
  label
}) => {
  const [createCustomFieldGroup, { error, loading: mutationLoading }] = useMutation<
    {
      group: CustomFieldDefinitionGroupModel;
    },
    {
      customFieldDefinitionGroup: Partial<CustomFieldDefinitionGroupModelInput>;
    }
  >(CREATE_CUSTOM_FIELD_GROUP);
  const { data, loading: queryLoading } = useQuery<
    {
      groups: CustomFieldDefinitionGroupsModel;
    },
    {
      entityType: CustomFieldDefinitionEntityTypeModel | undefined;
    }
  >(GET_CUSTOM_FIELD_GROUPS, { variables: { entityType }, skip: !entityType });

  const createNewGroup = useCallback(
    async (groupName: string) => {
      const resp = await createCustomFieldGroup({
        variables: { customFieldDefinitionGroup: { name: groupName, entityType } }
      });
      if (resp.data?.group && onChange) onChange(resp.data?.group as CustomFieldDefinitionGroupModel);
    },
    [createCustomFieldGroup, entityType, onChange]
  );

  const prevError = useRef<ApolloError | undefined>();

  useEffect(() => {
    if (onChangeError) {
      if (!prevError.current && !!error) {
        prevError.current = error;
        onChangeError(error?.message);
      } else if (!!prevError.current && !error) {
        prevError.current = undefined;
        onChangeError();
      }
    }
  }, [error, onChangeError]);

  return (
    <Select<string>
      label={label}
      disabled={disabled || mutationLoading || !entityType}
      dataTest={dataTest ?? 'group_select'}
      className={queryLoading || mutationLoading ? 'loading' : ''}
      options={filterNotEmpty(
        data?.groups?.customFieldDefinitionGroups?.map((group) => ({ value: group.uid, label: group.name }))
      )}
      placeholder="Select..."
      autoComplete
      loading={queryLoading || mutationLoading}
      onCreate={(groupName) => groupName && createNewGroup(groupName)}
      valueOptions={value ? { value: value.uid, label: value.name } : undefined}
      onOptionChange={(option) => onChange && onChange(option ? { uid: option.value, name: option.label } : undefined)}
    />
  );
};

export default CustomGroupPicker;
